import { createContext, FC, useContext, useMemo } from 'react';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import { IChildrenProps } from '../types';

interface IAlertContextState {
  info: (message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  warn: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  dismiss: () => void;
}

const AlertContext = createContext({} as IAlertContextState);

const AlertContextProvider: FC<IChildrenProps> = ({ children }) => {
  const values = useMemo<IAlertContextState>(
    () => ({
      info: (message, options) => toast.info(message, options),
      success: (message, options) => toast.success(message, options),
      warn: (message, options) => toast.warn(message, options),
      error: (message, options) => toast.error(message, options),
      dismiss: () => toast.dismiss(),
    }),
    [],
  );

  return (
    <AlertContext.Provider value={values}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        pauseOnHover
        closeOnClick
        draggable
        newestOnTop
        theme="colored"
      />
    </AlertContext.Provider>
  );
};

export default AlertContextProvider;

export const useAlertContext = (): IAlertContextState => {
  const context = useContext(AlertContext);

  if (context === undefined) {
    throw new Error('`useAlertContext` hook must be used within a `AlertContextProvider` component');
  }

  return context;
};
